import React, { useCallback, useState } from 'react';
import {
  Product,
  ExternalLinkSource,
  CreateExternalLinksResponse,
} from '../../services/api/domains/products/types';
import {
  modalStyles as styles,
  modalContentStyleFullHeight,
  modalBodyStyleFullHeight,
} from './styles';
import useAuth from '../../auth/auth';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import CollapsibleSection from './CollapsibleSection';
import OrangeBookPanel from './OrangeBookPanel';
import DMFPanel from './DMFPanel';
import CASRegistryPanel from './CASRegistryPanel';
import NDCPanel from './NDCPanel';
import LinkToSourceModal from './LinkToSourceModal';
import NamesTab from './fields/NamesTab';
import PrecedenceOrderTooltip from './fields/PrecedenceOrderTooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Edit, Save, X } from 'lucide-react';
import LgmButton from '../LgmButton';
import {
  toSectionFields,
  getAlwaysDisabledFields,
} from '../../domains/products/adapters';
import { Manufacturer } from 'services/api/domains/manufacturers/types';
import { useABTest } from '../../contexts/ABTestContext';
import {
  FIELD_TYPE_SECTIONS,
  DATA_SOURCE_SECTIONS,
  DETAILS_TAB_SECTIONS,
  ModalSectionConfig,
} from '../../domains/products/adapters/modalFieldAdapter';
import { useProductNameVariants } from '../../services/api/domains/products/hooks';
import {
  normalizeName,
  resolvePreviewSource,
} from '../../domains/products/utils/resolvePreviewSource';
import { groupVariantsByName } from '../../domains/products/utils/groupNameVariants';
import { GRAY_200, GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';

type ProductModalTab = 'details' | 'names';

// Issue #622, Sub-task 9.1 — human-readable labels for the Link-to-Source
// modal's title/panel text, matching the labels the three source panels
// already use (Task 8) rather than re-deriving them from ExternalLinkSource.
const EXTERNAL_LINK_SOURCE_LABELS: Record<ExternalLinkSource, string> = {
  orange_book: 'Orange Book',
  ndc: 'NDC',
  dmf: 'USDMF',
};

interface ProductEditProps {
  product: Product;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedProduct: Product) => void;
  close: () => void;
  allManufacturers: Manufacturer[];
}

const ProductModal: React.FC<ProductEditProps> = ({
  product,
  initialMode = 'VIEW',
  save,
  close,
  allManufacturers,
}) => {
  const { getTestValue } = useABTest();
  const productModalTabsEnabled = getTestValue('product_modal_tabs');
  const activeSections = getTestValue('modal_field_grouping')
    ? DATA_SOURCE_SECTIONS
    : FIELD_TYPE_SECTIONS;
  const [activeTab, setActiveTab] = useState<ProductModalTab>('names');
  const { isAdmin } = useAuth();
  // Issue #622, Task 9 — which source (if any) the Link-to-Source modal is
  // currently open for. Local UI state only, kept separate from
  // useEditableModal's edited-entity state so opening/closing this nested
  // modal can never touch the Product Modal's own unsaved-changes tracking.
  const [linkModalSource, setLinkModalSource] =
    useState<ExternalLinkSource | null>(null);
  // Issue #622, Sub-task 9.5 — a successful link-create refreshes the three
  // source panels. Deliberately a SEPARATE piece of state from
  // `editedProduct` rather than folded into it: `useEditableModal`'s
  // `hasUnsavedChanges` is a naive full-object `deepEqual` against
  // `originalEntity` (no field allowlist), so writing fresh ob_records/
  // dmf_records/ndc_records into `editedProduct` would flip
  // `hasUnsavedChanges` true and surface a misleading "unsaved changes"
  // state for data the admin never edited. Mirrors OrangeBookPanel's own
  // reason for NOT calling into `editedProduct` on break-link (its
  // `brokenIds` comment: "parent props only refresh on the next full
  // product fetch") — this state IS that full product fetch, scoped to
  // just the fields the three source panels read.
  const [linkedRefreshProduct, setLinkedRefreshProduct] =
    useState<Product | null>(null);
  const api = useApi();
  const productService = React.useMemo(() => new ProductService(api), [api]);

  // Use the hook for all modal logic
  const {
    editedEntity: editedProduct,
    setEditedEntity: setEditedProduct,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getPrimaryActionText,
    hasUnsavedChanges,
    isSaving,
  } = useEditableModal({
    entity: product,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: getAlwaysDisabledFields(),
    styles,
  });

  // Only fetched when the tab shell is on — the flag-off path never triggers this request.
  const nameVariantsQuery = useProductNameVariants(
    productModalTabsEnabled ? editedProduct.product_id : undefined
  );
  const nameVariants =
    nameVariantsQuery.status === 'success' ? nameVariantsQuery.data : [];

  // Header "Sourced from" preview (post-review redesign — moved out of the
  // Details tab body). Only meaningful once the tab shell is on; computed
  // from the same resolvePreviewSource used by the Names tab (Decision 3).
  // While the variants query hasn't settled yet and no explicit name is set,
  // resolvePreviewSource's empty-variants fallback would misreport "None" —
  // treat that window as loading instead of a real cascade result.
  const nameVariantsLoading =
    productModalTabsEnabled &&
    !editedProduct.preferred_product_name &&
    nameVariantsQuery.status !== 'success' &&
    nameVariantsQuery.status !== 'error';
  const headerSourcePreview = productModalTabsEnabled
    ? resolvePreviewSource(editedProduct.preferred_product_name, nameVariants)
    : null;
  const headerResolvedName = nameVariantsLoading
    ? null
    : headerSourcePreview?.variant?.name ||
      editedProduct.preferred_product_name ||
      null;

  // Compact checkmark/X status summary (post-review redesign) — replaces
  // three disabled Yes/No inputs with the same glyph convention the products
  // datagrid uses (renderBooleanCell). First pass per explicit request to
  // iterate visually; not tied to any specific FieldConfig.
  const renderStatusSummary = () => {
    // Mirrors CASRegistryPanel.tsx's own hasCommonChemistry gate exactly —
    // this badge and that panel's Common Chemistry block must agree on
    // whether the source is confirmed, or a product with only synonyms/
    // inchi_key/smile/replaced_rns data (no molecular_formula/inchi/
    // canonical_smile/uri) shows the panel block but a "false" badge here.
    const hasCommonChemistry = !!(
      editedProduct.uri ||
      (editedProduct.synonyms && editedProduct.synonyms.length > 0) ||
      editedProduct.molecular_formula ||
      editedProduct.inchi ||
      editedProduct.inchi_key ||
      editedProduct.smile ||
      editedProduct.canonical_smile ||
      (editedProduct.replaced_rns && editedProduct.replaced_rns.length > 0)
    );
    const hasFdaGsrs =
      editedProduct.cas_sources?.some(s => s.source === 'fda_gsrs') ?? false;
    const hasPubchem =
      editedProduct.cas_sources?.some(s => s.source === 'pubchem') ?? false;

    const indicators: { label: string; ok: boolean }[] = [
      { label: 'CAS Verified', ok: !!editedProduct.is_cas_verified },
      { label: 'CAS: FDA GSRS', ok: hasFdaGsrs },
      { label: 'CAS: PubChem', ok: hasPubchem },
      { label: 'CAS: Common Chemistry', ok: hasCommonChemistry },
      { label: 'Orange Book Linked', ok: !!editedProduct.is_fda_linked },
      { label: 'NDC Linked', ok: !!editedProduct.is_ndc_linked },
      { label: 'USDMF Linked', ok: !!editedProduct.is_dmf_linked },
    ];

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '8px',
          marginTop: '8px',
        }}>
        {indicators.map(({ label, ok }) => (
          <div
            key={label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: GRAY_900,
            }}>
            <span
              style={{
                fontWeight: 'bold',
                color: ok ? '#22c55e' : COLOR_DANGER,
              }}>
              {ok ? '✓' : '✗'}
            </span>
            {label}
          </div>
        ))}
      </div>
    );
  };

  // Issue #622, Sub-task 9.1 — opens the Link-to-Source modal scoped to one
  // source. Only sets local UI state (see linkModalSource above); the modal
  // itself renders as a sibling further down, so this never touches
  // useEditableModal's edited-entity/unsaved-changes state.
  const handleAddExternalLink = (source: ExternalLinkSource) => {
    setLinkModalSource(source);
  };

  // Issue #622, Sub-task 9.5 — "success closes the modal AND refreshes the
  // panels". `LinkToSourceModal` already closes itself; this re-fetches the
  // product and stores it in `linkedRefreshProduct` (see that state's own
  // comment for why it's kept separate from `editedProduct`) so
  // OrangeBookPanel/DMFPanel/NDCPanel immediately show the newly-linked
  // record(s) with their REAL, complete data — not the partial client-side
  // reconstruction `StagedExternalLinkRecord` uses for suggestion-added
  // records (see `externalLinkRecordAdapter.ts`'s data-gap comment). A
  // failed refetch is logged, not surfaced as a blocking error — the link
  // itself already committed successfully; the admin can still see it after
  // manually reopening the product.
  const handleLinked = useCallback(
    (_result: CreateExternalLinksResponse) => {
      productService
        .getById(editedProduct.product_id)
        .then(response => setLinkedRefreshProduct(response.data))
        .catch(err =>
          console.error(
            '❌ Failed to refresh product after linking external source record(s):',
            err
          )
        );
    },
    [productService, editedProduct.product_id]
  );

  const renderSections = (sections: ModalSectionConfig[]) =>
    sections.map((section, idx) => {
      const sectionFields = toSectionFields(
        { manufacturers: allManufacturers },
        section
      );
      // The "identity" section of whichever active config hosts the
      // OB/DMF/CAS/NDC source panels, regardless of section order or
      // whether it has any FieldConfig-driven fields of its own (the
      // tabs-on "External Sources" section has none — Sourced-from moved to
      // the modal header). Flagged explicitly on the section config itself
      // (issue #622, Sub-task 8.1) rather than matched by title — see
      // `ModalSectionConfig.hostsSourcePanels`'s doc comment in
      // modalFieldAdapter.ts for why the previous title-`Set` approach was
      // fragile.
      const hostsSourcePanels = !!section.hostsSourcePanels;
      // The tabs-on "Status" section also has no FieldConfig fields — it
      // renders the compact summary above instead. Flag-off's "Status"
      // section keeps its real fields (sectionFields.length > 0 there), so
      // this never affects the untouched regression path.
      const isStatusSummary =
        section.title === 'Status' && sectionFields.length === 0;
      if (sectionFields.length === 0 && !hostsSourcePanels && !isStatusSummary)
        return null;

      const fieldRenderer = isStatusSummary ? (
        renderStatusSummary()
      ) : sectionFields.length > 0 ? (
        <ModalFieldRenderer
          key={section.title}
          fields={sectionFields}
          entity={editedProduct}
          setEntity={setEditedProduct}
          mode={modalMode}
          focusedField={focusedField}
          setFocusedField={setFocusedField}
          isFieldDisabled={isFieldDisabled}
          getFloatingLabelStyle={getFloatingLabelStyle}
          getFloatingInputStyle={getFloatingInputStyle}
          styles={styles}
          missingMessage="Unknown"
        />
      ) : null;

      const sectionEl = section.collapsible ? (
        <CollapsibleSection
          key={section.title}
          title={section.title}
          defaultCollapsed={section.defaultCollapsed}>
          {fieldRenderer}
        </CollapsibleSection>
      ) : (
        <div key={section.title}>
          <h3
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: GRAY_500,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: `${idx === 0 ? 0 : 20}px 0 8px 0`,
              paddingBottom: '6px',
              borderBottom: `1px solid ${GRAY_200}`,
            }}>
            {section.title}
          </h3>
          {fieldRenderer}
        </div>
      );

      // Inject the source panels after the first section (Product Identity /
      // External Sources)
      if (hostsSourcePanels) {
        return (
          <React.Fragment key={`${section.title}-with-panels`}>
            {sectionEl}
            <OrangeBookPanel
              productId={editedProduct.product_id}
              obRecords={
                linkedRefreshProduct?.ob_records ??
                editedProduct.ob_records ??
                []
              }
              isAdmin={isAdmin}
              isEditMode={modalMode === 'EDIT'}
              onAddLink={() => handleAddExternalLink('orange_book')}
            />
            <DMFPanel
              productId={editedProduct.product_id}
              dmfRecords={
                linkedRefreshProduct?.dmf_records ??
                editedProduct.dmf_records ??
                []
              }
              isAdmin={isAdmin}
              isEditMode={modalMode === 'EDIT'}
              onAddLink={() => handleAddExternalLink('dmf')}
            />
            <NDCPanel
              productId={editedProduct.product_id}
              ndcRecords={
                linkedRefreshProduct?.ndc_records ??
                editedProduct.ndc_records ??
                []
              }
              isAdmin={isAdmin}
              isEditMode={modalMode === 'EDIT'}
              onAddLink={() => handleAddExternalLink('ndc')}
            />
            <CASRegistryPanel
              productId={editedProduct.product_id}
              isCasVerified={editedProduct.is_cas_verified ?? false}
              casNumber={editedProduct.cas_number}
              synonyms={editedProduct.synonyms}
              molecularFormula={editedProduct.molecular_formula}
              molecularMass={editedProduct.molecular_mass}
              inchi={editedProduct.inchi}
              inchiKey={editedProduct.inchi_key}
              smile={editedProduct.smile}
              canonicalSmile={editedProduct.canonical_smile}
              replacedRns={editedProduct.replaced_rns}
              uri={editedProduct.uri}
              casSources={editedProduct.cas_sources}
              casCandidates={editedProduct.cas_candidates}
              casOfficialCommonChemistry={
                editedProduct.cas_official_common_chemistry
              }
              casOfficialPubchem={editedProduct.cas_official_pubchem}
              casOfficialFdaGsrs={editedProduct.cas_official_fda_gsrs}
            />
          </React.Fragment>
        );
      }

      return sectionEl;
    });

  const renderNamesTab = () => {
    const groups = groupVariantsByName(nameVariants);
    const { variant: selectedVariant } = resolvePreviewSource(
      editedProduct.preferred_product_name,
      nameVariants
    );
    const selectedGroupKey = selectedVariant
      ? normalizeName(selectedVariant.name)
      : null;

    return (
      <NamesTab
        inputLabel="Product Name"
        hintText={
          <>
            Sets the LGM product name shown across the application. If left
            blank, the name is derived using the precedence order.{' '}
            <PrecedenceOrderTooltip size={12} />
          </>
        }
        value={editedProduct.preferred_product_name || ''}
        onChange={value =>
          setEditedProduct({
            ...editedProduct,
            preferred_product_name: value || null,
          })
        }
        onFocus={() => setFocusedField('preferred_product_name')}
        onBlur={() => setFocusedField(null)}
        isDisabled={isFieldDisabled('preferred_product_name')}
        styles={styles}
        getFloatingLabelStyle={getFloatingLabelStyle}
        getFloatingInputStyle={getFloatingInputStyle}
        fieldId="preferred_product_name"
        variantsLabel="Name Variants"
        variantsHintText="Select a variant to use as the Product Name, or type a custom name above."
        groups={groups}
        selectedGroupKey={selectedGroupKey}
        onSelectGroup={group =>
          setEditedProduct({
            ...editedProduct,
            preferred_product_name: group ? group.name : null,
          })
        }
        loadState={nameVariantsQuery.status}
      />
    );
  };

  return (
    <>
      <div style={styles.modalOverlayStyle}>
        <div data-testid="product-modal" style={modalContentStyleFullHeight}>
          <div style={styles.modalHeaderStyle}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <h2
                data-testid="modal-header-resolved-name"
                style={
                  productModalTabsEnabled
                    ? { ...styles.modalTitleStyle, marginBottom: 0 }
                    : styles.modalTitleStyle
                }>
                {productModalTabsEnabled ? (
                  nameVariantsLoading ? (
                    <em>Loading…</em>
                  ) : (
                    headerResolvedName || <em>Not set</em>
                  )
                ) : modalMode === 'EDIT' ? (
                  'Edit Product'
                ) : (
                  'Product Details'
                )}
              </h2>
              {productModalTabsEnabled && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: GRAY_500,
                  }}>
                  <span>Sourced from:</span>
                  <span
                    data-testid="modal-header-sourced-from-label"
                    style={{ fontWeight: 500 }}>
                    {nameVariantsLoading
                      ? 'Loading…'
                      : headerSourcePreview?.label}
                  </span>
                  <PrecedenceOrderTooltip size={13} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LgmButton
                variant="flat"
                color={modalMode === 'VIEW' ? 'warning' : 'save'}
                onClick={handleSave}
                disabled={
                  isSaving || (modalMode === 'EDIT' && !hasUnsavedChanges)
                }
                style={styles.modalHeaderIconTextButtonStyle}
                aria-label={getPrimaryActionText()}
                title={getPrimaryActionText()}>
                {modalMode === 'VIEW' ? <Edit size={16} /> : <Save size={16} />}
                {getPrimaryActionText()}
              </LgmButton>
              <LgmButton
                variant="flat"
                onClick={handleClose}
                disabled={isSaving}
                style={styles.modalCloseXStyle}
                hoverStyle={styles.modalCloseXHoverStyle}
                pressedStyle={styles.modalCloseXPressedStyle}
                aria-label="Close">
                <X size={16} />
              </LgmButton>
            </div>
          </div>

          {productModalTabsEnabled ? (
            <>
              <Tabs
                value={activeTab}
                onChange={(_event, newValue: ProductModalTab) =>
                  setActiveTab(newValue)
                }
                sx={{
                  minHeight: '28px',
                  borderBottom: `1px solid ${GRAY_200}`,
                  padding: 0,
                }}>
                <Tab
                  label="Names"
                  value="names"
                  sx={{
                    textTransform: 'none',
                    minHeight: '28px',
                    padding: '4px 12px',
                  }}
                />
                <Tab
                  label="Details"
                  value="details"
                  sx={{
                    textTransform: 'none',
                    minHeight: '28px',
                    padding: '4px 12px',
                  }}
                />
              </Tabs>
              <div
                style={{
                  ...modalBodyStyleFullHeight,
                  marginTop: '12px',
                  marginBottom: 0,
                }}>
                {activeTab === 'names'
                  ? renderNamesTab()
                  : renderSections(DETAILS_TAB_SECTIONS)}
              </div>
            </>
          ) : (
            <div style={{ ...modalBodyStyleFullHeight, marginBottom: 0 }}>
              {renderSections(activeSections)}
            </div>
          )}
        </div>
      </div>
      {linkModalSource && (
        <LinkToSourceModal
          source={linkModalSource}
          sourceLabel={EXTERNAL_LINK_SOURCE_LABELS[linkModalSource]}
          productId={editedProduct.product_id}
          onClose={() => setLinkModalSource(null)}
          onLinked={handleLinked}
        />
      )}
    </>
  );
};

export default React.memo(ProductModal);
export type { ModalMode };
